import { defineStore } from 'pinia'

// types
import type { UserAccessGrant } from '@/types/domain/user-access'

// Types
export interface AccessAllowedItem {
  id: number
  name: string
  regions?: number[]
  manufacturers?: number[]
  groupId?: number
  [key: string]: unknown
}

export interface AccessAllowed {
  regions: AccessAllowedItem[]
  manufacturers: AccessAllowedItem[]
  groups: AccessAllowedItem[]
  dealerships: AccessAllowedItem[]
  /** The session user's own access grants (from the permissions endpoint) */
  grants?: UserAccessGrant[]
}

export interface AccessSelected {
  regions: number[]
  manufacturers: number[]
  groups: number[]
  dealerships: number[]
}

/**
 * AccessStore - Manages user access permissions and selections
 * 
 * This store holds both the allowed access (from API) and selected access (user's choice),
 * with reactive state that automatically persists to localStorage for page refreshes.
 */
export const useAccessStore = defineStore('accessStore', {
  state: (): {
    accessAllowed: AccessAllowed
    accessSelected: AccessSelected
  } => ({
    accessAllowed: {
      regions: [],
      manufacturers: [],
      groups: [],
      dealerships: []
    },
    accessSelected: (() => {
      // Load from localStorage on initialization
      const stored = localStorage.getItem('accessSelected')
      if (!stored) {
        return {
          regions: [],
          manufacturers: [],
          groups: [],
          dealerships: []
        }
      }
      try {
        return JSON.parse(stored) as AccessSelected
      } catch {
        return {
          regions: [],
          manufacturers: [],
          groups: [],
          dealerships: []
        }
      }
    })()
  }),

  getters: {
    /**
     * Gets the selected dealership IDs as a comma-separated string
     * @returns Comma-separated string of dealership IDs
     */
    selectedDealershipIds(): string {
      return this.accessSelected.dealerships.join(',')
    },

    /**
     * Gets the first selected dealership ID
     * @returns First dealership ID or null
     */
    firstSelectedDealershipId(): number | null {
      return this.accessSelected.dealerships[0] ?? null
    },

    /**
     * Gets the count of selected dealerships
     * @returns Number of selected dealerships
     */
    selectedDealershipCount(): number {
      return this.accessSelected.dealerships.length
    },

    /**
     * Checks if any dealerships are selected
     * @returns True if at least one dealership is selected
     */
    hasSelectedDealerships(): boolean {
      return this.accessSelected.dealerships.length > 0
    }
  },

  actions: {
    /**
     * Sets the access allowed from API response
     * @param accessAllowed - Access allowed object from API
     */
    setAccessAllowed(accessAllowed: AccessAllowed): void {
      this.accessAllowed = accessAllowed
      
      // Filter accessSelected to only include items that are still in accessAllowed
      this.accessSelected.regions = this.accessSelected.regions.filter(region =>
        accessAllowed.regions.some(r => r.id === region)
      )
      this.accessSelected.manufacturers = this.accessSelected.manufacturers.filter(manufacturer =>
        accessAllowed.manufacturers.some(m => m.id === manufacturer)
      )
      this.accessSelected.groups = this.accessSelected.groups.filter(group =>
        accessAllowed.groups.some(g => g.id === group)
      )
      this.accessSelected.dealerships = this.accessSelected.dealerships.filter(dealership =>
        accessAllowed.dealerships.some(d => d.id === dealership)
      )

      // Auto-select single items if nothing is selected
      if (this.accessSelected.regions.length === 0 && accessAllowed.regions.length === 1) {
        this.accessSelected.regions.push(accessAllowed.regions[0].id)
      }
      if (this.accessSelected.manufacturers.length === 0 && accessAllowed.manufacturers.length === 1) {
        this.accessSelected.manufacturers.push(accessAllowed.manufacturers[0].id)
      }
      if (this.accessSelected.groups.length === 0 && accessAllowed.groups.length === 1) {
        this.accessSelected.groups.push(accessAllowed.groups[0].id)
      }
      if (this.accessSelected.dealerships.length === 0 && accessAllowed.dealerships.length === 1) {
        this.accessSelected.dealerships.push(accessAllowed.dealerships[0].id)
      }

      // Persist to localStorage
      this.persistToLocalStorage()
    },

    /**
     * Sets the access selected (user's choice)
     * @param accessSelected - Access selected object
     */
    setAccessSelected(accessSelected: AccessSelected): void {
      this.accessSelected = accessSelected
      this.persistToLocalStorage()
    },

    /**
     * Toggles an item in the access selection
     * @param category - The category to toggle (regions, manufacturers, groups, dealerships)
     * @param itemId - The ID of the item to toggle
     * @param value - Optional value to set (true/false), if not provided, toggles the current state
     */
    toggleItem(category: keyof AccessSelected, itemId: number, value?: boolean): void {
      const selectedArray = this.accessSelected[category]

      let newValue: boolean
      if (value != null) {
        newValue = value
      } else {
        newValue = !selectedArray.includes(itemId)
      }

      if (newValue) {
        // Add item to array if not already present
        if (!selectedArray.includes(itemId)) {
          selectedArray.push(itemId)
        }
      } else {
        // Remove item from array
        const index = selectedArray.indexOf(itemId)
        if (index > -1) {
          selectedArray.splice(index, 1)
        }
      }

      // Persist to localStorage
      this.persistToLocalStorage()
    },

    /**
     * Clears the access selected
     */
    clearAccessSelected(): void {
      this.accessSelected = {
        regions: [],
        manufacturers: [],
        groups: [],
        dealerships: []
      }
      localStorage.removeItem('accessSelected')
    },

    /**
     * Persists accessSelected to localStorage
     * Called automatically after any mutation
     */
    persistToLocalStorage(): void {
      localStorage.setItem('accessSelected', JSON.stringify(this.accessSelected))
    }
  }
})

